'use client';
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouter } from 'next/navigation';
import { trackSpecialistView, trackAddToFavorites } from '@/lib/analytics';
import api from '@/lib/api';
import { notify } from '@/lib/toast';
import { useAuthStore } from '@/store/auth.store';
import { useMessagesStore } from '@/store/messages.store';
import SpecialistCard from '@/components/specialists/SpecialistCard';
import { SpecialistPageSkeleton } from '@/components/ui/Skeleton';
import StarRating, { pluralReviews, formatSlotDate } from '@/components/specialists/StarRating';
import PortfolioGallery from '@/components/specialists/PortfolioGallery';
import ReviewsList from '@/components/specialists/ReviewsList';
import ServicesList from '@/components/specialists/ServicesList';
import BookingDrawer from '@/components/specialists/BookingDrawer';
import { getDisplayName } from '@/lib/displayName';
import ContactsBlock from '@/components/specialists/ContactsBlock';
import {
  Star, MapPin, Clock, Check, Heart, Briefcase,
  ChevronLeft, ShieldCheck, MessageSquare,
} from 'lucide-react';

const getAvatarColor = (name: string) => {
  const colors = [
    'from-brand to-[#3d8f96]',
    'from-purple-500 to-purple-700',
    'from-blue-500 to-blue-700',
    'from-rose-500 to-rose-700',
    'from-amber-500 to-amber-700',
  ];
  return colors[(name?.charCodeAt(0) ?? 0) % colors.length];
};

const toLocalDate = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const toLocalDateOffset = (ms: number) => {
  const n = new Date(Date.now() + ms);
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

export default function SpecialistProfilePage() {
  const params = useParams();
  const router = useRouter();
  const { isAuthenticated, user } = useAuthStore();
  const { setOpenWithUserId } = useMessagesStore();

  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingSvcId, setBookingSvcId] = useState<string | null>(null);

  const { data: specialist, isLoading } = useQuery({
    queryKey: ['specialist', params.id],
    queryFn: () => api.get(`/specialists/${params.id}`).then((r) => r.data.data),
  });

  useEffect(() => {
    if (specialist) {
      trackSpecialistView(String(params.id));
      if (specialist.username) {
        router.replace(`/u/${specialist.username}`);
      }
    }
  }, [specialist, params.id, router]);

  const { data: portfolio = [] } = useQuery({
    queryKey: ['portfolio', params.id],
    queryFn: () => api.get(`/specialists/${params.id}/portfolio`).then((r) => r.data.data),
    enabled: !!params.id,
  });

  const { data: favorites = [] } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => api.get('/favorites').then((r) => r.data.data),
    enabled: isAuthenticated(),
  });

  const specialistCategories = useMemo(() => {
    if (!specialist?.services) return [];
    const seen = new Set();
    return specialist.services
      .filter((s: any) => s.category && !seen.has(s.category.id) && seen.add(s.category.id))
      .map((s: any) => s.category);
  }, [specialist]);

  const primaryCategoryId = specialistCategories[0]?.id;

  const { data: similarData } = useQuery({
    queryKey: ['similar-specialists', primaryCategoryId],
    queryFn: () =>
      api
        .get('/specialists', { params: { category: specialistCategories[0]?.slug, limit: 4 } })
        .then((r) => r.data.data),
    enabled: !!primaryCategoryId,
  });

  const similarSpecialists = useMemo(
    () => (similarData ?? []).filter((s: any) => s.id !== params.id).slice(0, 3),
    [similarData, params.id],
  );

  const isFavorite = useMemo(
    () => favorites.some((f: any) => f.specialistId === params.id || f.specialist?.id === params.id),
    [favorites, params.id],
  );

  const isOwnProfile = specialist?.user?.id === user?.id;

  const favMutation = useMutation({
    mutationFn: () =>
      isFavorite
        ? api.delete(`/favorites/${params.id}`)
        : api.post('/favorites', { specialist_id: params.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
      notify.success(isFavorite ? 'Удалено из избранного' : 'Добавлено в избранное');
      if (!isFavorite) trackAddToFavorites();
    },
    onError: (err: any) => notify.apiError(err),
  });

  const queryClient = useQueryClient();

  const handleFavorite = () => {
    if (!isAuthenticated()) { router.push('/login'); return; }
    favMutation.mutate();
  };

  // Ближайший слот
  const availDateFrom = useMemo(() => toLocalDate(), []);
  const nearestDateTo = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const firstServiceId = specialist?.services?.[0]?.id ?? null;

  const { data: nearestData } = useQuery({
    queryKey: ['specialist-nearest', params.id, firstServiceId, availDateFrom, nearestDateTo],
    queryFn: () =>
      api
        .get(`/specialists/${params.id}/services/${firstServiceId}/availability`, {
          params: { date_from: availDateFrom, date_to: nearestDateTo },
        })
        .then((r) => r.data.data),
    enabled: !!(params.id && firstServiceId),
  });

  const nearestSlot = useMemo(() => {
    if (!nearestData?.availability?.length) return null;
    const first = nearestData.availability[0];
    if (!first?.slots?.length) return null;
    const date = first.date;
    const time = first.slots[0];
    const isToday = date === toLocalDate();
    const isTomorrow = date === toLocalDateOffset(86400000);
    const label = isToday ? 'сегодня' : isTomorrow ? 'завтра' : formatSlotDate(date).toLowerCase();
    return `${label} в ${time}`;
  }, [nearestData]);

  if (isLoading) return <SpecialistPageSkeleton />;

  if (!specialist) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center text-gray-500">
        Специалист не найден
      </div>
    );
  }

  const {
    user: spUser, services = [], rating, reviewsCount,
    city, bio, experienceYears, reviews = [], _count,
    workPhone, website, telegramUsername, workFormat, planBadge,
  } = specialist;

  const completedCount = _count?.bookings ?? 0;
  const spDisplayName = getDisplayName(spUser);
  const avatarColor = getAvatarColor(spDisplayName);
  const minPrice = services.length ? Math.min(...services.map((s: any) => Number(s.price))) : null;

  // workFormat → текст для шапки
  const workFormatLabel =
    workFormat === 'online' ? '💻 Онлайн'
    : workFormat === 'mobile' ? '🚗 Выезд к клиенту'
    : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">

      <button
        onClick={() => router.back()}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-brand transition-colors mb-5"
      >
        <ChevronLeft size={16} /> Назад к списку
      </button>

      {/* ───── Шапка профиля ───── */}
      <div className="bg-bg-surface rounded-2xl border border-border p-6 md:p-8 mb-5 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-6">

          {/* Аватар */}
          <div className="flex-shrink-0 self-center sm:self-start">
            {spUser?.avatarUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={spUser.avatarUrl} alt="" className="w-24 h-24 rounded-2xl object-cover shadow-sm" />
            ) : (
              <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${avatarColor} flex items-center justify-center text-white font-bold text-4xl shadow-sm`}>
                {spDisplayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
              <div>
                {/* Имя + верификация + тариф */}
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-text-primary">{spDisplayName}</h1>
                  {spUser?.isVerified && <ShieldCheck size={20} className="text-brand" />}
                  {planBadge && (
                    <span className={`text-sm font-semibold px-2 py-0.5 rounded-lg ${
                      planBadge === 'Про' ? 'bg-brand/10 text-brand'
                      : planBadge === 'Бизнес' ? 'bg-purple-100 text-purple-600'
                      : 'bg-blue-100 text-blue-600'
                    }`}>{planBadge}</span>
                  )}
                </div>

                {/* Город + опыт + формат работы */}
                <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-text-secondary">
                  {city && (
                    <span className="flex items-center gap-1">
                      <MapPin size={14} className="text-brand" /> {city}
                    </span>
                  )}
                  {experienceYears > 0 && (
                    <span className="flex items-center gap-1">
                      <Briefcase size={14} className="text-brand" />
                      {experienceYears} {experienceYears === 1 ? 'год' : experienceYears < 5 ? 'года' : 'лет'} опыта
                    </span>
                  )}
                  {workFormatLabel && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-brand/10 text-brand font-medium">
                      {workFormatLabel}
                    </span>
                  )}
                </div>

                {/* Контакты */}
                <ContactsBlock specialistId={String(params.id)} hasContacts={specialist.hasContacts ?? false} />

                {/* Категории */}
                {specialistCategories.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {specialistCategories.map((cat: any) => (
                      <span key={cat.id} className="inline-flex items-center gap-1 text-xs font-medium bg-brand/10 text-brand px-2.5 py-1 rounded-full">
                        <Briefcase size={11} /> {cat.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Кнопка избранного */}
              {!isOwnProfile && (
                <button
                  onClick={handleFavorite}
                  disabled={favMutation.isPending}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
                    isFavorite
                      ? 'bg-red-50 border-red-200 text-red-500 hover:bg-red-100'
                      : 'bg-bg-surface border-border text-text-secondary hover:border-brand hover:text-brand'
                  }`}
                >
                  <Heart size={16} className={isFavorite ? 'fill-red-500' : ''} />
                  {isFavorite ? 'В избранном' : 'В избранное'}
                </button>
              )}
            </div>

            {/* Рейтинг, выполнено, ближайший слот */}
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <div className="flex items-center gap-2 bg-bg-subtle rounded-xl px-3 py-2">
                <Star size={16} className="text-yellow-400 fill-yellow-400" />
                {reviewsCount > 0 ? (
                  <>
                    <span className="font-bold text-text-primary">{parseFloat(rating).toFixed(1)}</span>
                    <span className="text-gray-500 text-sm">({reviewsCount} {pluralReviews(reviewsCount)})</span>
                  </>
                ) : (
                  <span className="text-gray-400 text-sm">Нет отзывов</span>
                )}
              </div>
              {completedCount > 0 && (
                <div className="flex items-center gap-2 bg-bg-subtle rounded-xl px-3 py-2">
                  <Check size={16} className="text-green-600" />
                  <span className="text-sm text-text-ui"><span className="font-bold">{completedCount}</span> выполнено</span>
                </div>
              )}
              {nearestSlot && (
                <div className="flex items-center gap-2 bg-green-50 rounded-xl px-3 py-2">
                  <Clock size={16} className="text-green-600" />
                  <span className="text-sm text-green-700 font-medium">⚡ {nearestSlot}</span>
                </div>
              )}
            </div>

            {/* Био */}
            {bio ? (
              <p className="text-text-secondary leading-relaxed mt-4 text-sm md:text-base">{bio}</p>
            ) : (
              <p className="text-gray-400 italic leading-relaxed mt-4 text-sm">Специалист пока не добавил описание</p>
            )}
          </div>
        </div>
      </div>

      {/* ───── Услуги с аккордеоном ───── */}
      {services.length > 0 && (
        <div className="bg-bg-surface rounded-2xl border border-border mb-5 shadow-sm overflow-hidden" data-accordion>
          <div className="px-6 pt-5 pb-3">
            <h2 className="text-lg font-bold text-text-primary">Услуги и запись</h2>
            {minPrice !== null && (
              <p className="text-xs text-gray-400 mt-0.5">от {minPrice.toLocaleString()} ₽ · нажмите на услугу чтобы записаться</p>
            )}
          </div>
          <ServicesList
            services={services}
            onServiceClick={(id) => { setBookingSvcId(id); setBookingOpen(true); }}
          />
        </div>
      )}

      <BookingDrawer
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        specialistId={String(params.id)}
        specialist={specialist}
        services={services}
        initialServiceId={bookingSvcId}
      />

      {/* ───── Портфолио ───── */}
      <PortfolioGallery items={portfolio} />

      {/* ───── Отзывы ───── */}
      <ReviewsList reviews={reviews} />

      {/* ───── CTA написать ───── */}
      {!isOwnProfile && (
        <div className="bg-bg-surface rounded-2xl border border-border p-5 mb-5 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-text-primary text-sm">Остались вопросы?</p>
            <p className="text-xs text-gray-400 mt-0.5">Напишите специалисту — уточните детали перед записью</p>
          </div>
          <button
            onClick={() => { if (specialist?.user?.id) setOpenWithUserId(specialist.user.id); }}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-brand text-brand text-sm font-semibold hover:bg-brand/5 transition-all"
          >
            <MessageSquare size={15} /> Написать специалисту
          </button>
        </div>
      )}

      {/* ───── Похожие специалисты ───── */}
      {similarSpecialists.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-text-primary mb-4">Похожие специалисты</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {similarSpecialists.map((s: any) => (
              <SpecialistCard key={s.id} specialist={s} />
            ))}
          </div>
        </div>
      )}

      {/* ───── Sticky CTA мобиль ───── */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-bg-surface border-t border-border-subtle shadow-xl z-50">
        <div className="flex items-center gap-3 px-4 py-3 max-w-lg mx-auto">
          <div className="flex-1 min-w-0">
            {minPrice !== null && <div className="text-xs text-gray-400">от {minPrice.toLocaleString()} ₽</div>}
            {nearestSlot && <div className="text-xs text-green-600 font-medium truncate">⚡ {nearestSlot}</div>}
          </div>
          <button
            onClick={() => {
              const el = document.querySelector('[data-accordion]');
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="flex-shrink-0 px-5 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 transition-opacity shadow-sm"
            style={{ backgroundColor: 'var(--color-brand)' }}
          >
            Записаться
          </button>
        </div>
      </div>
      <div className="lg:hidden h-20" />

    </div>
  );
}
